<script lang="ts">
import type { VariantProps } from 'class-variance-authority'
import type { AcceptableValue, ToggleGroupRootProps } from 'reka-ui'
import type { CSSProperties, HTMLAttributes } from 'vue'
import { cva } from 'class-variance-authority'
import { createContext, ToggleGroupRoot, useForwardExpose, useForwardProps } from 'reka-ui'

export const toggleGroupVariants = cva('inline-flex items-stretch', {
  variants: {
    preset: {
      none: 'gap-1 items-center justify-center',
      dark: 'relative items-stretch p-1.25 rounded-lg bg-tertiary overflow-hidden',
      light: 'relative items-stretch p-1.25 rounded-lg bg-tertiary overflow-hidden',
    },
    size: {
      default: '',
      sm: 'h-8',
      md: 'h-10',
      lg: 'h-13',
    },
    layout: {
      none: '',
      fit: 'grid grid-flow-col auto-cols-fr w-fit self-start',
      fill: 'grid grid-flow-col auto-cols-fr w-full',
    },
  },
  defaultVariants: {
    preset: 'none',
    size: 'default',
    layout: 'none',
  },
})

export const toggleMarkerVariants = cva(
  'absolute top-0 left-0 z-0 rounded-md border border-solid shadow-md transition-[transform,width,height,background-color,border-color] duration-200 ease-out pointer-events-none',
  {
    variants: {
      preset: {
        dark: 'bg-solid border-transparent',
        light: 'bg-light border-disabled/20',
      },
      disabled: {
        true: 'bg-neutral-600/30! border-transparent! shadow-none!',
        false: '',
      },
    },
    defaultVariants: {
      preset: 'dark',
      disabled: false,
    },
  },
)

export type ToggleGroupVariants = VariantProps<typeof toggleGroupVariants>
export type ToggleMarkerVariants = VariantProps<typeof toggleMarkerVariants>

export interface ToggleGroupContext {
  preset?: ToggleGroupVariants['preset']
}
export const [injectToggleGroupContext, provideToggleGroupContext] = createContext<ToggleGroupContext>('ToggleGroup')

export type ToggleGroupProps = Omit<ToggleGroupRootProps, 'type' | 'modelValue' | 'defaultValue'> & {
  modelValue?: AcceptableValue
  defaultValue?: AcceptableValue
  class?: HTMLAttributes['class']
  preset?: 'dark' | 'light'
  size?: ToggleGroupVariants['size']
  layout?: ToggleGroupVariants['layout']
  required?: boolean
}
</script>

<script setup lang="ts">
import { reactiveOmit, useResizeObserver } from '@vueuse/core'
import { computed, nextTick, onMounted, ref, shallowRef, watch } from 'vue'
import { cn } from '#ui-kit/lib/utils'

const props = withDefaults(defineProps<ToggleGroupProps>(), {
  required: true,
})
const emits = defineEmits<{ 'update:modelValue': [value: AcceptableValue] }>()

provideToggleGroupContext({
  get preset() {
    return props.preset
  },
})

const delegatedProps = reactiveOmit(
  props,
  'class',
  'preset',
  'size',
  'layout',
  'required',
  'modelValue',
  'defaultValue',
)
const forwarded = useForwardProps(delegatedProps)

// Uncontrolled groups keep their value here; a bound `modelValue` wins when the parent provides one.
const internalValue = ref<AcceptableValue | undefined>(props.defaultValue)
const model = computed(() => (props.modelValue !== undefined ? props.modelValue : internalValue.value))
watch(
  () => props.modelValue,
  (value) => {
    internalValue.value = value
  },
)

function onUpdateModelValue(value: AcceptableValue | AcceptableValue[]) {
  if (Array.isArray(value)) return
  if (props.required && (value == null || value === '')) return
  internalValue.value = value
  emits('update:modelValue', value)
}

const { forwardRef, currentElement } = useForwardExpose()

const showMarker = computed(() => props.preset === 'dark' || props.preset === 'light')

const markerStyle = shallowRef<CSSProperties>({ opacity: 0 })
const markerAnimated = shallowRef(false)

function styleEqual(a: CSSProperties, b: CSSProperties) {
  return (
    a.opacity === b.opacity &&
    a.transform === b.transform &&
    a.width === b.width &&
    a.height === b.height &&
    a.backgroundColor === b.backgroundColor &&
    a.borderColor === b.borderColor
  )
}

function updateMarker() {
  if (!showMarker.value) return
  const root = currentElement.value as HTMLElement | undefined
  if (!root) return
  const selected = root.querySelector('[data-state="on"]') as HTMLElement | null
  if (!selected) {
    if (markerStyle.value.opacity !== 0) markerStyle.value = { opacity: 0 }
    return
  }
  const next: CSSProperties = {
    opacity: 1,
    transform: `translate3d(${selected.offsetLeft}px, ${selected.offsetTop}px, 0)`,
    width: `${selected.offsetWidth}px`,
    height: `${selected.offsetHeight}px`,
  }
  if (!props.disabled) {
    const cs = getComputedStyle(selected)
    const bg = cs.getPropertyValue('--marker-bg').trim()
    const border = cs.getPropertyValue('--marker-border').trim()
    if (bg) next.backgroundColor = bg
    if (border) next.borderColor = border
  }
  if (!styleEqual(markerStyle.value, next)) markerStyle.value = next
}

watch(
  model,
  () => {
    markerAnimated.value = true
    updateMarker()
  },
  { flush: 'post' },
)
onMounted(() => nextTick(updateMarker))
useResizeObserver(currentElement, updateMarker)

const DISABLED_CLASS = 'bg-neutral-600/30 cursor-not-allowed'
</script>

<template>
  <ToggleGroupRoot
    :ref="forwardRef"
    v-slot="slotProps"
    v-bind="forwarded"
    type="single"
    :model-value="model ?? null"
    :class="
      cn(
        toggleGroupVariants({ preset: preset ?? 'none', size, layout }),
        (preset === 'dark' || preset === 'light') && props.disabled && DISABLED_CLASS,
        props.class,
      )
    "
    @update:model-value="onUpdateModelValue"
  >
    <div
      v-if="showMarker"
      :class="cn(toggleMarkerVariants({ preset, disabled: !!props.disabled }), !markerAnimated && 'transition-none')"
      :style="markerStyle"
    />
    <slot v-bind="slotProps" />
  </ToggleGroupRoot>
</template>
